import _ from 'lodash';
import sumsumApi from '@/api/SumsumApi';
import dailyRoutineApi from '@/api/DailyRoutineApi';
import moment from 'moment';

const INTRO_DAILY_ROUTINE_DISMISSED_KEY = 'riseup.introDailyRoutineDismissed';
const TASKS_COMPLETED_DATE_KEY = 'riseup.dailyRoutineTasksCompletedDate';
const MAX_INITIAL_CARDS = 8;

const state = {
  dailyRoutines: [],
  dailyRoutinesNextChunk: [],
  showDailyRoutineViewer: false,
  specialCards: {},
  isIntroDismissed: false,
};

const UNIQUE_CARDS = [
  'INTRO',
  'DONE',
  'DONE_WITH_MORE_CARDS',
];

const getters = {
  isTasksCompletedForToday,
  doneCard: state => (state.dailyRoutinesNextChunk.length > 0 ? state.specialCards.DONE_WITH_MORE_CARDS : state.specialCards.DONE),
};

function todayDateString() {
  return moment().format('YYYY-MM-DD');
}

function isTasksCompletedForToday() {
  const completedDate = localStorage.getItem(TASKS_COMPLETED_DATE_KEY);
  if (!completedDate) {
    return false;
  }
  return completedDate === todayDateString();
}

const actions = {
  async initDailyRoutine({ commit, state }) {
    const tasksCompletedForToday = getters.isTasksCompletedForToday(state);

    const dailyRoutines = await sumsumApi.getDailyRoutines();
    const { customerDailyRoutines, specialCards } = _.reduce(dailyRoutines, (result, card) => {
      if (UNIQUE_CARDS.includes(card.type)) {
        result.specialCards[card.type] = card;
      } else {
        result.customerDailyRoutines.push(card);
      }
      return result;
    }, { customerDailyRoutines: [], specialCards: {} });

    commit('setSpecialCards', specialCards);
    const isIntroDismissed = localStorage.getItem(INTRO_DAILY_ROUTINE_DISMISSED_KEY) === 'true';
    commit('setIsIntroDismissed', isIntroDismissed);

    if (tasksCompletedForToday) {
      commit('setDailyRoutines', []);
      commit('setDailyRoutinesNextChunk', customerDailyRoutines);
    } else { // shouldResetCards true
      const customerDailyRoutinesFirstChunk = customerDailyRoutines.slice(0, MAX_INITIAL_CARDS);
      commit('setDailyRoutines', customerDailyRoutinesFirstChunk);
      commit('setDailyRoutinesNextChunk', _.drop(customerDailyRoutines, MAX_INITIAL_CARDS));
    }
  },

  async fetchDailyRoutinesNextChunk({ commit, state, dispatch }) {
    commit('setDailyRoutines', state.dailyRoutinesNextChunk);
    commit('setDailyRoutinesNextChunk', []);
    dispatch('markTasksUncompletedForToday');
  },

  async dismissDailyRoutine({ commit, state }, entityId) {
    await dailyRoutineApi.markDailyRoutineAsRead(entityId);
    const updateDrs = _.reject(state.dailyRoutines, dr => dr.entityId === entityId);
    commit('setDailyRoutines', updateDrs);
  },

  dismissIntro({ commit, state }) {
    commit('setIsIntroDismissed', true);
    localStorage.setItem(INTRO_DAILY_ROUTINE_DISMISSED_KEY, 'true');
  },

  markTasksCompletedForToday() {
    localStorage.setItem(TASKS_COMPLETED_DATE_KEY, todayDateString());
  },

  markTasksUncompletedForToday() {
    localStorage.removeItem(TASKS_COMPLETED_DATE_KEY);
  },

  dismissDailyRoutineByTransactionId({ state, dispatch }, transactionId) {
    const dailyRoutineToDismiss = _.find(state.dailyRoutines, dr => dr.transactionId === transactionId);
    const dailyRoutineFromNextChunkToDismiss = _.find(state.dailyRoutinesNextChunk, dr => dr.transactionId === transactionId);
    if (dailyRoutineToDismiss) {
      dispatch('dismissDailyRoutine', dailyRoutineToDismiss.entityId);
    }
    if (dailyRoutineFromNextChunkToDismiss) {
      dispatch('dismissDailyRoutine', dailyRoutineFromNextChunkToDismiss.entityId);
    }
  },
};

const mutations = {
  setDailyRoutines(state, dailyRoutines) {
    state.dailyRoutines = dailyRoutines;
  },
  setDailyRoutinesNextChunk(state, dailyRoutines) {
    state.dailyRoutinesNextChunk = dailyRoutines;
  },
  setShowDailyRoutineViewer(state, showDailyRoutineViewer) {
    state.showDailyRoutineViewer = showDailyRoutineViewer;
  },
  setIsIntroDismissed(state, isIntroDismissed) {
    state.isIntroDismissed = isIntroDismissed;
  },
  setSpecialCards(state, specialCards) {
    state.specialCards = specialCards;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
